import { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useLocation } from 'react-router';

import Error from 'components/common/forms/Error';
import SlideoutWithRouter from 'components/common/SlideoutWithRouter';
import Text from 'components/common/Text';
import CaretLeft from 'components/icons/navigation/CaretLeft';
import CloseX from 'components/icons/navigation/CloseX';
import { UserContext } from 'components/signUpFunnel/Contexts';
import SkipContainer from 'components/signUpFunnel/InternalSkip/SkipContainer';
import RecommendedBoxSummary from 'components/signUpFunnel/pickMeals/RecommendedBoxSummary';
import { Steps } from 'components/signUpFunnel/pickMeals/Steps';
import { BrandContext, TEMPO } from 'context/BrandContext';
import useFeatureFlag from 'hooks/useFeatureFlag';
import { ENABLE_INTERNAL_SKIP_FUNNEL } from 'lib/constants/featureFlags';
import { SKIP_PATHS } from 'lib/constants/signUp';
import * as AdaChat from 'utilities/adaChat';

const PickYourMealsSlideOut = () => {
  const { brand } = useContext(BrandContext);
  const isInternalSkipEnabled = useFeatureFlag(ENABLE_INTERNAL_SKIP_FUNNEL);

  const location = useLocation();
  const [showSlideOut, setShowSlideOut] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [skipError, setSkipError] = useState('');
  const [weeklyMeals, setWeeklyMeals] = useState();
  const [showBoxOptions, setShowBoxOptionsState] = useState(false);
  const { user } = useContext(UserContext);
  const setShowBoxOptions = useCallback(value => {
    setShowBoxOptionsState(value);
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('pym') === 'true') setTimeout(() => setShowSlideOut(true), 500);
    if (searchParams.get('box') === 'true') setCurrentStepIndex(Steps.length - 1);
  }, [location.search]);

  useEffect(() => {
    if (showSlideOut) {
      AdaChat.updateAdaIconVisibility({ hide: true });
    } else {
      AdaChat.updateAdaIconVisibility({ hide: false });
    }

    return () => {
      AdaChat.updateAdaIconVisibility({ hide: false });
    };
  }, [showSlideOut]);

  const prevShowSlideOut = useRef(false);

  useEffect(() => {
    if (
      prevShowSlideOut.current === true &&
      showSlideOut === false &&
      user?.email &&
      !location.pathname.startsWith('/pick-meals')
    ) {
      const url = new URL(window.location.href);
      url.searchParams.delete('pym');
      window.location.href = url.toString();
    }

    prevShowSlideOut.current = showSlideOut;
  }, [location.pathname, showSlideOut, user]);

  const dismissSlideOut = useCallback(() => {
    setShowSlideOut(false);
  }, []);

  const handleSlideOutNavigate = useCallback(path => {
    const stepIndexByPath = {
      [SKIP_PATHS.PREF_DIETARY]: 1,
      [SKIP_PATHS.BOX_PEOPLE]: 2,
      [SKIP_PATHS.BOX]: Steps.length - 1,
    };
    const stepIndex = stepIndexByPath[path];
    if (stepIndex !== undefined) setCurrentStepIndex(stepIndex);
  }, []);

  if (brand !== TEMPO) return null;

  const currentStep = Steps[currentStepIndex];
  const StepContent = currentStep.content;

  const goNext = () => {
    if (currentStepIndex < Steps.length - 1) setCurrentStepIndex(currentStepIndex + 1);
  };

  const goBack = () => {
    if (currentStepIndex > 0) setCurrentStepIndex(currentStepIndex - 1);
  };

  return (
    <SlideoutWithRouter
      id="pick-your-meals-slide-out"
      labelId="pick-your-meals-slide-out-label"
      show={showSlideOut}
      desktopWidth="w-full"
      closeCallback={() => setShowSlideOut(false)}
      hideTopBar={true}
      slideoutContainerClassname="h-[670px] bottom-0 top-auto bg-tempo-gray-40 rounded-t-2xl shadow-lg bp1:rounded-md flex flex-col"
      transitionClassname="slideout-from-bottom"
    >
      <div className="flex-1 overflow-y-auto px-4">
        <div className="mx-auto mt-6 bp1:max-w-3xl">
          {currentStepIndex === 0 ? (
            <div className="flex justify-end">
              <button onClick={() => setShowSlideOut(false)} aria-label="close slide out">
                <CloseX className="h-5 w-5" />
              </button>
            </div>
          ) : (
            <div className="flex justify-between">
              <button onClick={goBack} className="flex items-center pr-0 text-xs font-medium">
                <CaretLeft className="h-4 w-4" />
                <Text
                  htmlElement="span"
                  className="sr-only bp1:not-sr-only"
                  style={{ paddingLeft: '0.25rem' }}
                >
                  Back
                </Text>
              </button>
              <Text>Question {currentStepIndex}/5</Text>
            </div>
          )}
          <div className="mx-auto max-w-lg text-center">
            <h2 className="h6 mb-2 pt-6 text-tempo-black-100">{currentStep.title}</h2>
            <p className="textSm mb-4 text-charcoal-90">{currentStep.subtitle}</p>
            {isInternalSkipEnabled && (
              <>
                <SkipContainer
                  dismissSlideOut={dismissSlideOut}
                  isPickMeals
                  mealPlan={TEMPO}
                  onSlideOutNavigate={handleSlideOutNavigate}
                  renderInSlideOut
                  showSkipTargetsOnCurrentStep={currentStepIndex === 0}
                  setError={setSkipError}
                />
                <Error className="mt-2 text-center" message={skipError} show={Boolean(skipError)} />
              </>
            )}

            <div className="mb-2">
              <StepContent
                goNext={goNext}
                pathname={currentStep.pathname}
                setWeeklyMeals={setWeeklyMeals}
                weeklyMeals={weeklyMeals}
                setShowBoxOptions={setShowBoxOptions}
                showBoxOptions={showBoxOptions}
              />
            </div>
          </div>
          <div className="flex justify-end"></div>
        </div>
      </div>
      {currentStep.id === 'recommendations' && weeklyMeals && (
        <RecommendedBoxSummary weeklyMeals={weeklyMeals} showBoxOptions={showBoxOptions} />
      )}
    </SlideoutWithRouter>
  );
};

export default PickYourMealsSlideOut;
